import React, { useEffect, useState } from 'react';

import { useWindowSize } from '../../hooks/useWindowSize';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import {
  TourContainer,
  TourArrow,
  TourHeader,
  TourText,
  TourContent,
  NextButton,
  IconButtonContainer,
  ButtonIcon,
} from './styles';

const getDefaultHeightMultiplier = (arrow) => {
  if (arrow === 'left' || arrow === 'right') {
    return 0.5;
  } else if (arrow === 'top') {
    return 1;
  }
  return 0;
};

const getDefaultWidthMultiplier = (arrow) => {
  if (arrow === 'top' || arrow === 'bottom') {
    return 0.5;
  } else if (arrow === 'left') {
    return 1;
  }
  return 0;
};

// Get position offset relative to the window and arrow placement
const getTourStepPosition = (tourData, step, layout) => {
  let tourStep = tourData[step];
  if (layout === 'wide' && tourStep.wideScreen) {
    tourStep = { ...tourStep, ...tourStep.wideScreen };
  }
  const targetElem = document.getElementById(tourStep.elementId);
  if (targetElem) {
    targetElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const targetRect = targetElem.getBoundingClientRect();
    const arrow = tourStep.arrow ?? 'top';
    let offsetHeight =
      window.pageYOffset +
      targetRect.top +
      targetRect.height * (tourStep?.heightMultiplier ?? getDefaultHeightMultiplier(arrow));
    let offsetWidth =
      window.pageXOffset +
      targetRect.left +
      targetRect.width * (tourStep?.widthMultiplier ?? getDefaultWidthMultiplier(arrow));
    return { offsetHeight, offsetWidth, arrow };
  }
};

/**
 * tourData example: [
 *  {
 *    title: 'The tour step title',
 *    text: 'The tour step text content',
 *    elementId: 'id of element tour step is about',
 *    heightMultiplier: Number in range [0, 1],
 *    widthMultiplier: Number in range [0, 1],
 *    arrow: 'top', 'bottom', 'left', or 'right'. Arrow placement relative to the tour box,
 *  }
 * ]
 * The multipliers are percentage relative the size of the element tour step is indicating.
 * If you want arrow to point up towards middle of element set arrow: 'top', widthMultiplier: 0.5, heightMultiplier: 1
 * if no arrow given default will be 'top'
 * if no multipliers given default will try to point to middle of object from outside of it
 *
 * @param {*} tourData should be a list with objects containing data for tour steps
 */
export default function Tour({ tourData, completed, setCompleted, isDarkMode, layout }) {
  const [step, setStep] = useState(0);
  const [offset, setOffset] = useState(undefined);

  const windowSize = useWindowSize();
  const scrollPosition = useScrollPosition();

  // Try to render every 200ms for 10 tries if the element for the first tourStep has not rendered itself yet.
  useEffect(() => {
    let tourStartIntervalCount = 0;
    let tourStartInterval = setInterval(() => {
      const position = getTourStepPosition(tourData, step, layout);
      if (position) {
        clearInterval(tourStartInterval);
        setOffset(position);
      } else if (tourStartIntervalCount++ > 10) {
        clearInterval(tourStartInterval);
        nextTourStep();
      }
    }, 200);
    return () => clearInterval(tourStartInterval);
  });

  useEffect(() => {
    if (completed) return;
    const position = getTourStepPosition(tourData, step, layout);
    if (position) {
      setOffset(position);
    }
  }, [windowSize, completed, layout, step, tourData]);

  const closeTheTour = () => {
    setCompleted(true);
    setStep(0);
  };

  const nextTourStep = () => {
    if (tourData.length <= step + 1) {
      return closeTheTour();
    }
    // If the next steps element does not exists, use the next step after that.
    for (let i = step + 1; i < tourData.length; i++) {
      const position = getTourStepPosition(tourData, i, layout);
      if (position) {
        setStep(i);
        setOffset(position);
        return;
      }
    }
    closeTheTour();
  };

  return (
    offset && (
      <TourContainer
        offsetHeight={offset.offsetHeight - scrollPosition.y}
        offsetWidth={offset.offsetWidth - scrollPosition.x}
        orientation={offset.arrow}
      >
        {/* Arrow placement will be handled by flex-direction */}
        <TourArrow orientation={offset.arrow} />
        <TourContent isDarkMode={isDarkMode} orientation={offset.arrow}>
          <TourHeader>
            <h4>{tourData[step].title}</h4>
            <IconButtonContainer title={'Close the tour'} onClick={closeTheTour}>
              <ButtonIcon icon={'fa fa-times'} />
            </IconButtonContainer>
          </TourHeader>
          <TourText>{tourData[step].text}</TourText>
          <NextButton title={'Get another tip!'} onClick={nextTourStep}>
            {step < tourData.length - 1 ? 'Next' : 'Done'}
          </NextButton>
        </TourContent>
      </TourContainer>
    )
  );
}
